import React, {useContext, useEffect, useReducer, useRef, useState} from 'react';
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import {Attachment, CartItem, FloorPlan, FloorPlanOption, OptionValue, Settings} from "../SharedTypes";
import ImageButton from "../components/ImageButton";
import TableFooter from "@material-ui/core/TableFooter";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Clear from "@material-ui/icons/Clear";
import OptionSelect from "../components/OptionSelect";
import {CartContext, CartItemAction} from '../components/CartContext';
import OptionValueDialog, {colorMap} from "../components/OptionValueDialog";
import SubmitIcon from "@material-ui/icons/Send";
import BigNumber from "bignumber.js";
import {formatMoney, getOptionPrice} from "../shared";
import CloudDownload from "@material-ui/icons/CloudDownload";
import {BackendContext} from "../components/BackendContext";
import BackendApi from "../BackendApi";
import Swal from "sweetalert2";
import EmailOptionsDialog, {EmailOptionForm} from "../components/EmailOptionsDialog";
import {Link as RouterLink} from 'react-router-dom';
import ModalDialog from "../components/ModalDialog";
import Badge from "@material-ui/core/Badge";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {TextField} from "@material-ui/core";
import NumberFormat from "react-number-format";
import SalesTaxDialog from "../components/SalesTaxDialog";
import ChevronLeft from "@material-ui/icons/ChevronLeft";

const useStyles = makeStyles(theme => ({
    title: {
        flexGrow: 1,
        textAlign: 'left',
        color: theme.palette.text.secondary,
    },
    container: {
        margin: 'auto',
        maxWidth: theme.breakpoints.values.lg,
        padding: theme.spacing(2),
    },
    card: {
        width: 200,
        display: 'inline-block',
        marginLeft: theme.spacing(0.25),
        marginRight: theme.spacing(0.25),
        marginBottom: theme.spacing(1.5),
        [theme.breakpoints.down('sm')]: {
            marginLeft: theme.spacing(0.75),
            marginRight: theme.spacing(0.75),
        }
    },
    optionContainer: {
        paddingLeft: 0,
        paddingRight: 0,
        textAlign: 'center',
    },
    cart: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(),
        /*marginLeft: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            margin: 0,
        },*/
    },
    center: {textAlign: 'center'},
    infoColor: {
        color: theme.palette.info.dark
    },
    dangerColor: {
        color: theme.palette.error.dark
    },
    actionButtons: {
        textAlign: 'left',
        marginTop: theme.spacing(2),
    },
    logo: {
        maxWidth: 320,
        maxHeight: 80,
    },
    additionalPhoto: {
        display: 'inline-block !important',
        height: '120px !important',
        marginRight: '1.5rem',
        marginTop: '1rem',
        padding: theme.spacing(1),
        '&:hover': {
            cursor: 'pointer',
            border: '1px solid #bbb',
            borderRadius: 2,
        }

    }
}));

export function cartItemsReducer(cartItems: CartItem[], action: CartItemAction): CartItem[] {

    function getSortBy(options: FloorPlanOption[]): (a: CartItem, b: CartItem) => number {
        return (a, b) => options.indexOf(a.floorPlanOption) - options.indexOf(b.floorPlanOption);
    }

    switch (action.type) {
        case "AddItem":
            return cartItems.concat([action.item]).sort(getSortBy(action.options));
        case "UpdateItem":
            return cartItems.map((item, index) => index === action.index ? action.item : item);
        case "RemoveItem":
            return cartItems.filter((_, index) => index !== action.index);
        case "LoadItems":
            return action.items.sort(getSortBy(action.options));
    }
}

export function getOptionValueDesc(optionValue: OptionValue, formData: Record<string, string>): string {
    const pricing = optionValue.pricing;
    const quantity = parseInt(formData.quantity) || 1;
    return (
        (quantity > 1)
            ? `${optionValue.name} (${pricing?.unitName ? `${quantity} ${pricing.unitName}` : `x${quantity}`})`
            : optionValue.name
    ) + ((optionValue.showCommentBox && formData.comments) ? ` - ${formData.comments}` : '');
}

function getOptionValueImage(optionValue: OptionValue, formData: Record<string, string>): string {
    const selectedColor = colorMap[formData.comments];
    if (optionValue.autoFillColors && selectedColor) {
        const canvas = document.createElement('canvas');
        canvas.width = 80;
        canvas.height = 80;
        document.body.append(canvas);
        const ctx = canvas.getContext("2d");
        if (ctx) {
            ctx.fillStyle = selectedColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const url = canvas.toDataURL();
            document.body.removeChild(canvas);
            return url;
        } else {
            document.body.removeChild(canvas);
        }
    }
    return optionValue.photo.url || 'https://place-hold.it/300x300/777';
}

async function createPDF(backendApi: BackendApi, floorPlan: FloorPlan, cartItems: CartItem[],
                         shippingAmount: BigNumber, shippingMiles: number,
                         salesTaxAmount: BigNumber, salesTax: number,
                         emailForm?: EmailOptionForm) {
    const [pdfMake, pdfFonts] = await Promise.all([
        import('pdfmake/build/pdfmake.js'),
        import('pdfmake/build/vfs_fonts.js')
    ]);
    const w = window as any;
    w.pdfMake = pdfMake;
    w.pdfMake.vfs = pdfFonts.pdfMake.vfs;
    const content = [
        // {image: await getEncodedImage(cartItems[0].floorPlanOption.photo.url), fit: [400, 400]} as any,
        {
            text: new Date().toLocaleString(),
            fontSize: 10,
            bold: true,
            color: 'gray',
            margin: [0, 10, 0, 0] /* margin: [left, top, right, bottom] */
        },
        ...emailForm ? [
            {text: `${emailForm.name} ${emailForm.email}`, margin: [5, 10, 0, 0]},
            {text: `${emailForm.city} ${emailForm.state} ${emailForm.zipCode}`, margin: [5, 5, 0, 0]},
            {text: 'Comments:', margin: [5, 5, 0, 0]},
            {text: emailForm.comments, margin: [10, 0, 0, 0], bold: true},
        ] : [],
        {text: 'Option Selections for ' + floorPlan.name, fontSize: 18, bold: true, margin: [0, 5, 0, 10]},
        {
            table: {
                widths: [200, '*', 200],
                body: [
                    ['', 'Name', 'Amount'],
                    ...(await Promise.all(cartItems.map(async item => {
                        const photoUrl = getOptionValueImage(item.optionValue, item.fieldData);
                        return [
                            {
                                alignment: 'center',
                                margin: [0, 4, 0, 4],
                                image: photoUrl.startsWith('http') ? await backendApi.encodeImage(photoUrl) : photoUrl,
                                fit: [100, 100]
                            } as any,
                            {
                                margin: [0, 4, 0, 4],
                                text: getOptionValueDesc(item.optionValue, item.fieldData),
                                italics: true,
                                color: 'gray'
                            },
                            {text: formatMoney(getOptionPrice(item.optionValue, item.fieldData))}
                        ]
                    }))),
                    ...(salesTax === 0 ? [] : [[
                        '', {
                            text: `Estimated Sales Tax (${new BigNumber(salesTax * 100).toFormat(3)}%)`,
                            color: 'gray'
                        }, {
                            text: `${formatMoney(salesTaxAmount)}`
                        }]]),
                    ...(!shippingMiles ? [] : [['', {
                        text: `Estimated Shipping (${new BigNumber(shippingMiles).toFormat(0)} Miles)`,
                        color: 'gray'
                    }, {
                        text: `${formatMoney(shippingAmount)}`
                    }]]),
                    ['', {text: 'Total Quote', color: 'gray'}, {
                        text: formatMoney(cartItems.map(item => getOptionPrice(item.optionValue, item.fieldData))
                            .reduce((a, b) => a.plus(b), new BigNumber(0))
                            .plus(shippingAmount || new BigNumber(0))), bold: true
                    }]
                ],
            },
            layout: 'lightHorizontalLines'
        } as any,
    ];
    return pdfMake.createPdf({
        content,
        /*pageBreakBefore(currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) {
            return currentNode.pageNumbers.length > 1;
        }*/
    });
}

async function exportPDF(backendApi: BackendApi, floorPlan: FloorPlan, cartItems: CartItem[],
                         shippingAmount: BigNumber, shippingMiles: number,
                         salesTaxAmount: BigNumber, salesTax: number) {
    const pdf = await createPDF(backendApi, floorPlan, cartItems, shippingAmount, shippingMiles, salesTaxAmount, salesTax);
    pdf.download(`${floorPlan.name}_options.pdf`.toLowerCase().trim().replace(/\s/g, '_'));
}

async function sendPDF(backendApi: BackendApi, floorPlan: FloorPlan, cartItems: CartItem[],
                       shippingAmount: BigNumber, shippingMiles: number,
                       salesTaxAmount: BigNumber, salesTax: number,
                       emailForm: EmailOptionForm) {
    const pdf = await createPDF(backendApi, floorPlan, cartItems, shippingAmount, shippingMiles,
        salesTaxAmount, salesTax, emailForm);
    const blob = await new Promise(res => pdf.getBlob(res as any));
    await backendApi.submitLead(blob, emailForm.name, emailForm.email, emailForm.phone, floorPlan.id + '', floorPlan.name);
    await Swal.fire({
        title: 'Success',
        text: 'Your options have been submitted!',
        icon: 'success',
        // confirmButtonText: 'OK'
    });
}

const CartPage = ({
                      floorPlanName,
                      floorPlanId,
                      listingPage
                  }: { floorPlanName?: string, floorPlanId?: string, listingPage?: string }) => {
    const classes = useStyles(),
        [settings, setSettings] = useState<Settings | undefined>(),
        [option, setOption] = useState<FloorPlanOption | undefined>(),
        [cartItems, dispatchCartItemAction] = useReducer(cartItemsReducer, [] as CartItem[]),
        [editItem, setEditItem] = useState<{ cartItem: CartItem, index: number } | undefined>(),
        backendApi = useContext(BackendContext),
        [floorPlan, setFloorPlan] = useState<FloorPlan | undefined>(),
        [showPhoto, setShowPhoto] = useState<Attachment | undefined>(),
        [showEmailDialog, setShowEmailDialog] = useState(false),
        [milesFrom, setMilesFrom] = useState<number | undefined>(),
        [salesTaxPercent, setSalesTaxPercent] = useState<number | undefined>(),
        [showSalesTaxDialog, setShowSalesTaxDialog] = useState(false),
        cartItemTotal = cartItems.map(item => getOptionPrice(item.optionValue, item.fieldData))
            .reduce((a, b) => a.plus(b), new BigNumber(0)),
        shippingAmount = new BigNumber(milesFrom || 0).times(new BigNumber(settings?.pricePerMile || 0)),
        salesTaxAmount = new BigNumber(salesTaxPercent || 0).times(cartItemTotal),
        theme = useTheme(),
        isXs = useMediaQuery(theme.breakpoints.down('xs')),
        isSm = useMediaQuery(theme.breakpoints.down('sm')),
        STORAGE_KEY = floorPlan ? `${floorPlan.id}_OPTIONS` : undefined,
        selectedOptions = cartItems.reduce((obj, cartItem) => ({
            ...obj, [cartItem.floorPlanOption.name]: (obj[cartItem.floorPlanOption.name] || 0) + 1
        }), {} as Record<string, number>),
        optionContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        backendApi.getSettings().then(settings => {
            setSettings(settings);
            const findFloorPlanPromise = floorPlanId
                ? backendApi.findFloorPlanById(floorPlanId)
                : (floorPlanName ? backendApi.findFloorPlan(floorPlanName)
                    : Promise.resolve(undefined));
            findFloorPlanPromise.then(floorPlan => {
                setFloorPlan(floorPlan);
                setOption(undefined);
                if (settings.showAllOptionValues && floorPlan?.options) {
                    setOption({
                        name: '',
                        values: floorPlan.options.reduce((values, option) =>
                            values.concat(option.values), [] as OptionValue[])
                    } as FloorPlanOption);
                }
            });
        });
    }, [backendApi, floorPlanName, floorPlanId]);

    // Load in the previously selected options
    useEffect(() => {
        if (STORAGE_KEY && floorPlan) {
            try {
                const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '')
                    // This will remove options that were previously selected but are no longer available
                    .map((item: CartItem) => {
                        const floorPlanOption = (floorPlan?.options || []).find(o => o.name === item.floorPlanOption.name),
                            optionValue = (floorPlanOption?.values || []).find(v => v.name === item.optionValue.name);
                        if (floorPlanOption && optionValue) {
                            return {floorPlanOption, optionValue, fieldData: item.fieldData};
                        } else {
                            return undefined;
                        }
                    }).filter((item: CartItem | undefined) => !!item);
                dispatchCartItemAction({type: 'LoadItems', items, options: floorPlan.options});
            } catch (ignored) {
            }
        }
    }, [STORAGE_KEY, floorPlan]);

    // Save cart items each time they change
    useEffect(() => {
        if (STORAGE_KEY) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
        }
    }, [STORAGE_KEY, cartItems])

    if (!floorPlan) {
        return <div style={{textAlign: 'center', marginTop: '2rem'}}><CircularProgress/></div>
    }

    return (
        <CartContext.Provider value={{cartItems, dispatchCartItemAction}}>
            <div className={classes.container}>
                <div style={isSm ? {marginBottom: 0} : {marginBottom: '-3rem', position: 'relative', zIndex: 1}}>
                    {settings?.logo ? <RouterLink to={listingPage || '/'}>
                            <img src={settings.logo.url} alt={settings.title} className={classes.logo}/>
                        </RouterLink>
                        : <Typography component="h1" variant="h4">{settings?.title}</Typography>}
                    {listingPage ? <Breadcrumbs>
                        <Link color="inherit" component={RouterLink} to={listingPage}>
                            Floor Plans
                        </Link>
                        <Typography color="textPrimary">{floorPlan.name}</Typography>
                    </Breadcrumbs> : null}
                </div>

                <Grid container spacing={2}>
                    <Grid item sm={12} md={5}>
                        <Paper className={classes.cart} style={{marginTop: '3rem'}}>
                            <div className={classes.center}>
                                {floorPlan.photo?.url
                                    ? <img src={floorPlan.photo.url} alt={floorPlan.name} style={{maxWidth: '100%'}}/>
                                    : null}
                            </div>
                            <Typography component="h1" variant="h4">{floorPlan.name}</Typography>
                            <Typography variant="body2" color="textSecondary" component="p"
                                        dangerouslySetInnerHTML={{__html: (floorPlan.description || '').replace(/\n/g, '')}}/>

                            {(floorPlan.additionalPhotos || []).map((photo, i) =>
                                <img key={photo.url} src={photo.url} alt={`Additional ${i + 1}`}
                                     className={classes.additionalPhoto} onClick={() => setShowPhoto(photo)}/>)}
                        </Paper>

                    </Grid>
                    <Grid item sm={12} md={7} className={classes.optionContainer}>
                        <div ref={optionContainerRef} style={{position: 'relative'}}/>
                        <Typography component="h3" variant="h5" className={classes.title}
                                    style={{minHeight: '3rem', lineHeight: '3rem', position: 'relative', zIndex: 1}}>
                            {(option && !settings?.showAllOptionValues) ? <Tooltip title="Back"><IconButton
                                onClick={() => setOption(settings?.showAllOptionValues ? option : undefined)}><ChevronLeft/></IconButton></Tooltip> : ''}
                            {(option ? option.name : '') || (settings?.availableOptionsTitle || 'Available Options')}
                        </Typography>
                        <Paper style={isSm ? {padding: '0.5rem'} : {
                            overflow: 'auto',
                            padding: '1rem'
                        }}>
                            {option
                                ? <OptionSelect floorPlanOption={option}
                                                onBack={() =>
                                                    settings?.showAllOptionValues ? undefined : setOption(undefined)}
                                                options={floorPlan.options}/>
                                : <>
                                    {floorPlan.options.map((option, index) =>
                                        <Paper key={index} className={classes.card}>
                                            {Boolean(selectedOptions[option.name]) ?
                                                <Badge color="primary" style={{width: '100%'}} variant="dot"
                                                       badgeContent={selectedOptions[option.name]}
                                                       anchorOrigin={{vertical: 'top', horizontal: 'left',}}>
                                                    <ImageButton title={option.name} src={option.photo.url}
                                                                 onClick={() => {
                                                                     setOption(option);
                                                                     /*setTimeout(() => {
                                                                         window.scrollTo(0, optionContainerRef?.current?.offsetTop || 0);
                                                                     }, 50);*/
                                                                 }}/>
                                                </Badge>
                                                : <ImageButton title={option.name} src={option.photo.url}
                                                               onClick={() => {
                                                                   setOption(option);
                                                                   /*setTimeout(() => {
                                                                       window.scrollTo(0, optionContainerRef?.current?.offsetTop || 0);
                                                                   }, 50);*/
                                                               }}/>}
                                        </Paper>)}
                                </>}
                        </Paper>
                        <Typography component="h3" variant="h5" className={classes.title}
                                    style={{marginBottom: '0.25rem', marginTop: '0.75rem'}}>
                            Selected Options
                        </Typography>
                        <Paper className={classes.cart}>
                            <div style={isSm ? {} : {maxHeight: '70vh', overflow: 'auto'}}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell/>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell/>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cartItems.map((item: CartItem, index) => <React.Fragment key={index}>
                                            {isXs ? <TableRow>
                                                <TableCell colSpan={4} style={{borderBottom: 'none'}}>
                                                    <img
                                                        src={getOptionValueImage(item.optionValue, item.fieldData)}
                                                        alt={item.optionValue.name}
                                                        style={{maxWidth: 120, maxHeight: 120}}/>
                                                </TableCell>
                                            </TableRow> : null}
                                            <TableRow hover={!isXs}>
                                                {!isXs ? <TableCell align="center">
                                                    <img
                                                        src={getOptionValueImage(item.optionValue, item.fieldData)}
                                                        alt={item.optionValue.name}
                                                        style={{maxWidth: 80, maxHeight: 80}}/>
                                                </TableCell> : null}
                                                <TableCell colSpan={isXs ? 2 : 1}>
                                                    <strong>{item.floorPlanOption.name}</strong><br/>
                                                    <Link style={{cursor: 'pointer'}}
                                                          onClick={() => setEditItem({cartItem: item, index})}>
                                                        {getOptionValueDesc(item.optionValue, item.fieldData)}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    {formatMoney(getOptionPrice(item.optionValue, item.fieldData))}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Remove">
                                                        <IconButton
                                                            onClick={() => dispatchCartItemAction({
                                                                type: 'RemoveItem',
                                                                index
                                                            })}
                                                            size="small">
                                                            <Clear className={classes.dangerColor}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>)}
                                    </TableBody>
                                    <TableFooter>
                                        {salesTaxPercent ? <TableRow>
                                            <TableCell/>
                                            <TableCell>
                                                Estimated Sales Tax {salesTaxPercent
                                                ? `(${(salesTaxPercent * 100).toFixed(2)}%)` : ''}</TableCell>
                                            <TableCell>
                                                <strong>
                                                    {formatMoney(salesTaxAmount)}
                                                </strong>
                                            </TableCell>
                                            <TableCell/>
                                        </TableRow> : null}
                                        {settings?.shippingOrigin ? <TableRow>
                                            <TableCell/>
                                            <TableCell>Estimated Shipping</TableCell>
                                            <TableCell>
                                                <strong>
                                                    {formatMoney(shippingAmount)}
                                                </strong>
                                            </TableCell>
                                            <TableCell/>
                                        </TableRow> : null}
                                        <TableRow>
                                            <TableCell/>
                                            <TableCell>Total Quote</TableCell>
                                            <TableCell>
                                                <strong>
                                                    {formatMoney(cartItemTotal.plus(shippingAmount).plus(salesTaxAmount))}
                                                </strong>
                                            </TableCell>
                                            <TableCell/>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </div>

                            <div style={{margin: '1rem 0', verticalAlign: 'center', textAlign: 'left'}}>
                                {settings?.shippingOrigin ?
                                    <NumberFormat id="milesFrom"
                                                  label={`Miles to Job Site`}
                                                  value={milesFrom || ''}
                                                  thousandSeparator style={{width: 300}}
                                                  customInput={TextField} decimalScale={0} min={0}
                                                  onValueChange={e => setMilesFrom(e.floatValue)}
                                                  helperText={`Enter your miles from ${settings?.shippingOrigin} to your job site. Used to estimate shipping.`}
                                    /> : null}
                                {settings?.hasZipTaxKey ?
                                    <Button color="primary" onClick={() => setShowSalesTaxDialog(true)}
                                            style={{marginTop: '1rem', display: 'block'}}>
                                        Estimate Sales Tax {salesTaxPercent
                                        ? ` (${new BigNumber(salesTaxPercent * 100).toFormat(3)}%)` : ''}
                                    </Button> : null}
                            </div>

                            <div className={classes.actionButtons}>
                                <Button variant="contained" color="primary" startIcon={<SubmitIcon/>}
                                        onClick={() => setShowEmailDialog(true)}>Submit My Inquiry</Button>
                                <Button color="primary" startIcon={<CloudDownload/>} style={{marginLeft: '1rem'}}
                                        onClick={() => exportPDF(backendApi, floorPlan, cartItems, shippingAmount,
                                            milesFrom || 0, salesTaxAmount, salesTaxPercent || 0)}>
                                    Export PDF</Button>
                            </div>

                        </Paper>
                    </Grid>
                </Grid>
                <OptionValueDialog floorPlanOption={editItem?.cartItem.floorPlanOption}
                                   optionValue={editItem?.cartItem.optionValue}
                                   fieldData={editItem?.cartItem.fieldData}
                                   submitLabel="Update Option"
                                   onCancel={() => setEditItem(undefined)}
                                   onSave={(fieldData) => {
                                       dispatchCartItemAction({
                                           type: 'UpdateItem', item: {
                                               floorPlanOption: editItem?.cartItem.floorPlanOption!,
                                               optionValue: editItem?.cartItem.optionValue!,
                                               fieldData,
                                           }, index: editItem?.index!
                                       });
                                       // Close the Dialog
                                       setEditItem(undefined);
                                       // Scroll to top of page
                                       // window.scroll(0, 0);
                                   }}/>
                <EmailOptionsDialog onCancel={() => setShowEmailDialog(false)} open={showEmailDialog}
                                    onSubmit={data => {
                                        setShowEmailDialog(false);
                                        return sendPDF(backendApi, floorPlan, cartItems, shippingAmount, milesFrom || 0, salesTaxAmount, salesTaxPercent || 0, data)
                                    }}/>
                <ModalDialog open={Boolean(showPhoto)} onClose={() => setShowPhoto(undefined)}
                             maxWidth="lg" title="" content={showPhoto
                    ? <div>
                        <IconButton
                            style={{position: 'absolute', top: 2, right: 2}}
                            onClick={() => {
                                setShowPhoto(undefined);
                            }}
                            size="small">
                            <Clear/>
                        </IconButton>
                        <img src={showPhoto.url} alt="Additional"
                             style={{
                                 maxWidth: '100%', maxHeight: 'calc(100vh - 115px)',
                                 display: 'block', margin: 'auto'
                             }}/>
                    </div> : null}/>

                <SalesTaxDialog open={Boolean(showSalesTaxDialog)}
                                onCancel={() => setShowSalesTaxDialog(false)}
                                onSelect={(salesTaxPercent) => {
                                    setShowSalesTaxDialog(false);
                                    setSalesTaxPercent(salesTaxPercent);
                                }}/>
            </div>
        </CartContext.Provider>
    );
};

export default CartPage;